import React, { useRef, useState, useEffect } from 'react'
import { Formik } from 'formik'
import { ExperienceInputField } from '../form'
import { Form as RBForm } from 'react-bootstrap'
import * as Yup from 'yup'
import classNames from 'classnames'
import { useNotificationService } from '@abroad/components'
import { useParams } from 'react-router-dom'
import Imgix from 'react-imgix'
import QuestHOC from './QuestHOC'
import API from '../../utils/API'
import axios from 'axios'
import ImageCropModal from '../common/ImageCropModal'
import {
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const defaultValues = {
  title: '',
  youtubeLink: '',
  posterImageFileName: null,
}

const normalizeMainVideo = (mainVideo) => ({
  title: mainVideo?.title || '',
  youtubeLink: mainVideo?.youtubeLink || '',
  posterImageFileName: mainVideo?.posterImageFileName || '',
})

const validationSchema = Yup.object().shape({
  youtubeLink: Yup.string().matches(
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    'Invalid YouTube URL',
  ),
})

const buildMainVideoPayload = (values) => ({
  mainVideo: {
    title: values.title || '',
    youtubeLink: values.youtubeLink || '',
    posterImageFileName: values.posterImageFileName || '',
  },
})

const QuestMainVideo = ({ questDetail, updateQuestDetails }) => {
  const [initialValues, setInitialValues] = useState(() =>
    normalizeMainVideo(questDetail?.mainVideo || defaultValues),
  )
  const valuesRef = useRef(initialValues)
  const [uploading, setUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [showRemoveImageConfirm, setShowRemoveImageConfirm] = useState(false)
  const pendingRemoveRef = useRef(null)

  useEffect(() => {
    const next = normalizeMainVideo(questDetail?.mainVideo || defaultValues)
    setInitialValues(next)
    valuesRef.current = next
  }, [questDetail])

  const imageRef = useRef(null)
  const inputResetRef = useRef(null)
  const Notification = useNotificationService()
  const { questId } = useParams()

  const getImage = () => {
    if (imageRef.current) {
      imageRef.current.value = ''
      imageRef.current.click()
    }
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()

    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    if (
      imageFile.type === 'image/webp' ||
      imageFile.name.toLowerCase().endsWith('.webp')
    ) {
      Notification.showNotification(
        'WebP format is not supported. Please use JPG, JPEG, or PNG format.',
        'danger',
      )
      return false
    }
    return true
  }

  const openCropModal = (imageFile, resetInput) => {
    if (!imageFile) return
    if (!validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(imageFile)
    setShowImageCropModal(true)
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    setIsDraggingImage(false)
    resetImageInput()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      openCropModal(files[0])
      e.dataTransfer.clearData()
    }
  }

  const uploadQuestImage = async (imageFile) => {
    try {
      const contentType = imageFile.name
        .substr(imageFile.name.lastIndexOf('.') + 1)
        .toUpperCase()

      const { data } = await API.AdminAPI.quests.getVideoPosterSignedURL(
        questId,
        contentType,
      )
      if (data) {
        await axios.put(data.url.url, imageFile, {
          headers: {
            'Content-Type': data.url.contentType,
          },
        })
        setUploading(false)
        return data.fileName
      } else {
        throw new Error('Image upload failed')
      }
    } catch (e) {
      throw e
    } finally {
      setTimeout(() => {
        setUploading(false)
      }, 850)
    }
  }

  const handleRemoveImage = async (setFieldValue, values) => {
    try {
      setIsRemoving(true)
      setFieldValue('posterImageFileName', '')
      updateQuestDetails(
        {
          mainVideo: {
            title: values?.title || '',
            youtubeLink: values?.youtubeLink || '',
            posterImageFileName: '',
          },
        },
        { silent: true },
      )
    } catch (e) {
      Notification.showNotification('Failed to remove image', 'danger')
    } finally {
      setTimeout(() => {
        setIsRemoving(false)
      }, 300)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        await runQueuedFormikSave({
          values,
          valuesRef,
          buildPayload: buildMainVideoPayload,
          resetForm,
          setInitialValues: (latest) => {
            const next = normalizeMainVideo(latest)
            setInitialValues(next)
            valuesRef.current = next
          },
          save: (payload) => updateQuestDetails(payload, { silent: true }),
        })
      }}>
      {(formik) => {
        const { handleSubmit, values, setFieldValue, dirty } = formik
        valuesRef.current = formik.values
        const hasPoster = !!values?.posterImageFileName
        const uploadBusy = uploading || isRemoving
        const fieldBlur = () => queueAutosaveOnBlur(formik)
        const persistVideo = (next) => {
          updateQuestDetails(buildMainVideoPayload(next), { silent: true })
        }

        return (
          <form onSubmit={handleSubmit} aria-busy={uploadBusy || undefined}>
            <FormikDirtyReporter id='content-video' dirty={dirty} />
            <div className='experience-video-editor'>
              <div>
                <div className='experience-f'>
                  <ExperienceInputField
                    name='title'
                    label='Title'
                    placeholder='Welcome to the Quest'
                    showErrorMessage={false}
                    displayFlex={false}
                    capitalizeFirstLetter
                    onBlur={fieldBlur}
                  />
                </div>
                <div className='experience-f mt-12px'>
                  <ExperienceInputField
                    name='youtubeLink'
                    label='YouTube link'
                    placeholder='https://www.youtube.com/watch?v='
                    showErrorMessage
                    displayFlex={false}
                    onBlur={fieldBlur}
                  />
                </div>
              </div>
              <div className='experience-f'>
                <label className='experience-f-label'>Poster image</label>
                <div className='experience-img-slot' data-ratio='16:9'>
                  <div
                    className={classNames('experience-img-frame', {
                      'is-empty': !hasPoster && !uploadBusy,
                      'is-dragging': isDraggingImage,
                    })}
                    onClick={() => {
                      if (!uploadBusy) getImage()
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!uploadBusy) getImage()
                      }
                    }}>
                    {uploadBusy ? (
                      <span
                        className='experience-img-hover'
                        style={{ opacity: 1 }}>
                        {isRemoving ? 'Removing…' : 'Uploading…'}
                      </span>
                    ) : (
                      <span className='experience-img-hover'>
                        {isDraggingImage
                          ? 'Drop image to upload'
                          : hasPoster
                          ? 'Click to replace'
                          : 'Upload image'}
                      </span>
                    )}

                    {hasPoster ? (
                      values.posterImageFileName.startsWith('data:') ? (
                        <img
                          src={values.posterImageFileName}
                          alt='Main Video Poster'
                        />
                      ) : (
                        <Imgix
                          imgixParams={{
                            fit: 'crop',
                            crop: 'center',
                            ar: '16:9',
                          }}
                          src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${values.posterImageFileName}`}
                          attributeConfig={{
                            src: 'data-src',
                            srcSet: 'data-srcset',
                            sizes: 'data-sizes',
                          }}
                          htmlAttributes={{
                            src: `${process.env.REACT_APP_IMG_SOURCE}/quest/${questId}/${values.posterImageFileName}?ar=16:9&fit=crop&crop=center&fill=blur&blur=300&px=16&auto=format`,
                          }}
                          width={'100%'}
                          alt='Main Video Poster'
                          className={classNames('lazyload', {
                            itenaryImage: uploading,
                          })}
                        />
                      )
                    ) : !uploadBusy ? (
                      <>
                        <b>Upload image</b>
                        <small>JPG, JPEG or PNG · cropped to 16:9</small>
                      </>
                    ) : null}

                    {hasPoster && !uploadBusy ? (
                      <button
                        type='button'
                        className='experience-img-remove'
                        aria-label='Remove image'
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          pendingRemoveRef.current = () =>
                            handleRemoveImage(setFieldValue, values)
                          setShowRemoveImageConfirm(true)
                        }}>
                        ×
                      </button>
                    ) : null}

                    <RBForm.Control
                      type='file'
                      size='lg'
                      placeholder='Select File'
                      accept='image/jpg, image/jpeg, image/png'
                      onChange={async (e) => {
                        const target = e.target
                        const files = e.target.files
                        if (files && files?.length > 0) {
                          openCropModal(files[0], () => {
                            if (target) target.value = ''
                          })
                        }
                      }}
                      style={{ display: 'none' }}
                      name='posterImageFileName'
                      sm='10'
                      ref={imageRef}
                    />
                  </div>
                </div>
                <ImageCropModal
                  show={showImageCropModal}
                  onHide={handleCropModalClose}
                  file={selectedImageFile}
                  onConfirm={async (croppedImageFile) => {
                    if (!croppedImageFile) return
                    setShowImageCropModal(false)
                    setUploading(true)
                    const reader = new FileReader()
                    reader.onloadend = async () => {
                      setFieldValue(`posterImageFileName`, reader.result)
                      try {
                        const fileName = await uploadQuestImage(
                          croppedImageFile,
                        )
                        setFieldValue(`posterImageFileName`, fileName)
                        persistVideo({
                          ...values,
                          posterImageFileName: fileName,
                        })
                      } catch (e) {
                        Notification.showNotification(
                          'Image upload failed',
                          'danger',
                        )
                      } finally {
                        setUploading(false)
                        setSelectedImageFile(null)
                        setIsDraggingImage(false)
                        resetImageInput()
                      }
                    }
                    reader.readAsDataURL(croppedImageFile)
                  }}
                  isUploading={uploading}
                  aspect={16 / 9}
                  ratioLabel='16:9'
                  title='Crop Image'
                />
              </div>
            </div>
            <button type='submit' hidden aria-hidden />
            <ExperienceConfirmModal
              show={showRemoveImageConfirm}
              onHide={() => {
                if (!isRemoving) {
                  setShowRemoveImageConfirm(false)
                  pendingRemoveRef.current = null
                }
              }}
              onConfirm={async () => {
                const run = pendingRemoveRef.current
                pendingRemoveRef.current = null
                setShowRemoveImageConfirm(false)
                if (run) await run()
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
              isLoading={isRemoving}
            />
          </form>
        )
      }}
    </Formik>
  )
}

export default QuestHOC(QuestMainVideo)
